<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <FancyServerTable
      v-if="isStateLoaded"
      ref="fancyTableRef"
      :data="supplierBankAccountsTableData"
      :columns="supplierBankAccountsTableHeaders"
      :enable-filters="true"
      :enable-pagination="true"
      :enable-column-visibility="true"
      :enable-column-pinning="true"
      :initial-column-pinning="
        currentSavedState?.columnPinning || {
          left: [],
          right: ['actions'],
        }
      "
      :initial-column-visibility="currentSavedState?.columnVisibility || {}"
      :initial-column-order="currentSavedState?.columnOrder || []"
      :initial-column-sizing="currentSavedState?.columnSizing || {}"
      :initial-column-filters="currentSavedState?.columnFilters || []"
      :initial-sorting="currentSavedState?.sorting ?? []"
      :total-rows="totalRows"
      :page-index="pageIndex"
      :page-size="pageSize"
      :auto-filter="true"
      enable-row-selection
      :get-row-id="row => String(row.id)"
      @update:row-selection="handleRowSelectionChange"
      :loading="isLoading"
      @update:state="debouncedHandleTableStateChange"
      @fetch-data="fetchSupplierBankAccounts"
    >
      <template #actions>
        <SupplierBankAccountsTableBulkActions
          :selectedBankAccounts="selectedSupplierBankAccounts"
          :customerId="customerId"
          @reloadTable="getSupplierBankAccountsTableList"
          @clearSelection="handleClearSelection"
        />
      </template>
      <template #config-manager>
        <FancyTableConfigManager
          :saved-configs="savedTableConfigs"
          :current-config="currentSavedState"
          :is-loading="false"
          :can-save-state="canSaveState"
          @save-config="handleSaveConfig"
          @update-config="handleUpdateConfig"
          @apply-config="handleApplyConfig"
          @delete-config="handleDeleteConfig"
          @mark-as-default="handleMarkAsDefault"
        />
      </template>
    </FancyServerTable>
  </div>
</template>

<script setup lang="ts">
import { h, onMounted, ref } from 'vue';
import type { BankAccountInList } from '@/_helpers/interfaces/bank-accounts/bank-account-in-list.interface';
import { useI18n } from 'vue-i18n';
import { useBankAccountStore } from '@/stores/bank-account.store';
import {
  FancyServerTable,
  type FancyTableColumnDef,
  type ServerTableOptions,
  useTableRenderers,
  FieldType,
  FilterType,
  FancyTableConfigManager,
} from '@/components/shared/fancyTables';
import { printFormat } from 'iban-ts';

import SupplierBankAccountsTableRowActions from './SupplierBankAccountsTableRowActions.vue';
import SupplierBankAccountsTableBulkActions from './SupplierBankAccountsTableBulkActions.vue';
import { useFancyTableConfig } from '@/_helpers/composables/fancy-table-config.composable';

const fancyTableRef = ref<any>(null);
const selectedSupplierBankAccounts = ref<BankAccountInList[]>([]);
// Cache to store all selected row data across pages
const selectedRowsCache = ref<Map<string, any>>(new Map());
const { GetFieldRenderer } = useTableRenderers();

const { getBankAccountsList } = useBankAccountStore();
const { t } = useI18n();

const props = defineProps<{
  customerId?: string | null;
}>();

// Map filters to API params
const mapFiltersToApiParams = (filters: any[]) => {
  const params: any = {};

  filters.forEach(filter => {
    const { id, value } = filter;

    switch (id) {
      case 'bank_name':
        if (value) params.bank_name = value;
        break;
      case 'swift_code':
        if (value) params.swift_code = value;
        break;
      case 'owner_name':
        if (value) params.owner_name = value;
        break;
      case 'iban':
        if (value) params.iban = value;
        break;
      case 'supplier_name':
        if (value) params.supplier_name = value;
        break;
      case 'supplier_number':
        if (value) params.supplier_number = value;
        break;
      case 'created_on':
        if (value?.from) params.created_after = value.from;
        if (value?.to) params.created_before = value.to;
        break;
      default:
        break;
    }
  });

  return params;
};

// Map sorting to API param
const mapSortingToApiParam = (sorting: any[]): string => {
  if (sorting.length === 0) return '';

  const sort = sorting[0];
  const { id, desc } = sort;
  let backendField = id;

  // Map frontend field names to backend field names
  switch (id) {
    case 'supplier_name':
      backendField = 'supplier__name';
      break;
    case 'supplier_number':
      backendField = 'supplier__supplier_number';
      break;
    default:
      backendField = id;
  }

  return desc ? `-${backendField}` : backendField;
};

// Server-side fetch function
const fetchSupplierBankAccounts = async (options?: ServerTableOptions) => {
  isLoading.value = true;

  try {
    const currentPageIndex = options?.pageIndex ?? 0;
    const currentPageSize = options?.pageSize ?? 10;
    const filters = options?.filters ?? [];
    const sorting = options?.sorting ?? [];

    // Update pagination state
    pageIndex.value = currentPageIndex;
    pageSize.value = currentPageSize;

    // Map filters to API params
    const filterParams = mapFiltersToApiParams(filters);

    // Map sorting to API param
    const orderingParam = mapSortingToApiParam(sorting);

    const apiParams = {
      page: currentPageIndex + 1,
      page_size: currentPageSize,
      supplier_accounts_only: true,
      customer: props.customerId || '',
      ...filterParams,
      ...(orderingParam && { ordering: orderingParam }),
    };

    const response = await getBankAccountsList(apiParams);

    supplierBankAccountsTableData.value = response.results;
    totalRows.value = response.count;
  } catch (error) {
    console.error('Error fetching supplier bank accounts:', error);
    supplierBankAccountsTableData.value = [];
    totalRows.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const getSupplierBankAccountsTableList = async () => {
  // This is now a wrapper for fetchSupplierBankAccounts for backward compatibility
  await fetchSupplierBankAccounts();
};

// Initialize the table config composable
const {
  savedTableConfigs,
  currentSavedState,
  canSaveState,
  debouncedHandleTableStateChange,
  handleSaveConfig,
  handleApplyConfig,
  handleUpdateConfig,
  handleDeleteConfig,
  handleMarkAsDefault,
  isStateLoaded,
} = useFancyTableConfig({
  tableName: 'supplier-bank-accounts',
  fancyTableRef,
  tableLoader: getSupplierBankAccountsTableList,
});

const isLoading = ref(false);
const supplierBankAccountsTableData = ref<BankAccountInList[]>([]);
const totalRows = ref(0);
const pageIndex = ref(0);
const pageSize = ref(10);

const supplierBankAccountsTableHeaders: FancyTableColumnDef<BankAccountInList>[] = [
  {
    header: t('suppliers.titles.bank_name'),
    accessorKey: 'bank_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('suppliers.titles.swift_placeholder'),
    accessorKey: 'swift_code',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('suppliers.titles.account_holder'),
    accessorKey: 'owner_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 250,
  },
  {
    header: t('suppliers.titles.iban'),
    accessorKey: 'iban',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    filterFn: 'iban',
    cell: (info: any) => {
      return printFormat(info.getValue() as string);
    },
    size: 300,
  },
  {
    header: t('suppliers.titles.supplier_name'),
    accessorKey: 'supplier_name',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: (info: any) => {
      return info.getValue() || '--';
    },
    size: 250,
  },
  {
    header: t('suppliers.titles.supplier_number'),
    accessorKey: 'supplier_number',
    enableSorting: true,
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.TEXT,
    cell: GetFieldRenderer(FieldType.TEXT),
    size: 200,
  },
  {
    header: t('suppliers.titles.created_at'),
    enableSorting: true,
    accessorKey: 'created_on',
    enableColumnFilter: true,
    enableHiding: true,
    fieldType: FieldType.DATE,
    filterType: FilterType.DATE_RANGE,
    cell: GetFieldRenderer(FieldType.DATE),
    filterFn: 'dateRange',
    size: 200,
  },
  {
    header: t('common.actions.actions'),
    accessorKey: 'actions',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false,
    hideFromColumnConfig: true,
    cell: info => {
      return h(SupplierBankAccountsTableRowActions, {
        bankAccount: info.row.original,
        onReloadTable: getSupplierBankAccountsTableList,
      });
    },
    size: 120,
  },
];

const handleClearSelection = () => {
  fancyTableRef.value.resetRowSelection();
  selectedRowsCache.value.clear();
  selectedSupplierBankAccounts.value = [];
};

const handleRowSelectionChange = (selection: any[]) => {
  // Update cache: add newly selected rows from current page
  selection.forEach(row => {
    selectedRowsCache.value.set(String(row.id), row);
  });

  // Remove deselected rows: find rows in current page that are no longer selected
  const currentPageIds = new Set(supplierBankAccountsTableData.value.map(row => String(row.id)));
  const selectedIds = new Set(selection.map(row => String(row.id)));

  currentPageIds.forEach(id => {
    if (!selectedIds.has(id)) {
      selectedRowsCache.value.delete(id);
    }
  });

  // Update selectedSupplierBankAccounts with all cached rows
  selectedSupplierBankAccounts.value = Array.from(selectedRowsCache.value.values());
};

onMounted(async () => {
  await getSupplierBankAccountsTableList();
});
</script>
